import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import './DripSchedule.css';

const DripSchedule = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const RELEASE_TYPES = [
    { value: 'immediate', label: t('dripSchedule.immediate') },
    { value: 'days_after_enrollment', label: t('dripSchedule.daysAfterEnrollment') },
    { value: 'fixed_date', label: t('dripSchedule.fixedDate') },
  ];

  const [courseName, setCourseName] = useState('');
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showWeeklyModal, setShowWeeklyModal] = useState(false);
  const [weeklyDay, setWeeklyDay] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, modulesRes] = await Promise.all([
          api.get(`/courses/${courseId}`),
          api.get(`/courses/${courseId}/modules`),
        ]);
        setCourseName(courseRes.data.title);
        setModules(
          modulesRes.data.map((m) => ({
            id: m.id,
            title: m.title,
            order_index: m.order_index,
            release_type: m.release_type || 'immediate',
            drip_days: m.drip_days ?? 0,
            release_date: m.release_date ? m.release_date.split('T')[0] : '',
            is_preview: m.is_preview || false,
            lesson_count: parseInt(m.lesson_count) || 0,
          }))
        );
      } catch (err) {
        setError(t('dripSchedule.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId]);

  const updateModule = (moduleId, field, value) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        const updated = { ...m, [field]: value };
        if (field === 'release_type' && value === 'immediate') {
          updated.drip_days = 0;
          updated.release_date = '';
        }
        return updated;
      })
    );
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      for (const module of modules) {
        const payload = {
          release_type: module.release_type,
          drip_days: module.release_type === 'days_after_enrollment' ? parseInt(module.drip_days) || 0 : 0,
          release_date: module.release_type === 'fixed_date' ? module.release_date || null : null,
          is_preview: module.is_preview,
        };
        await api.put(`/courses/modules/${module.id}`, payload);
      }
      setSuccess(t('dripSchedule.saveSuccess'));
    } catch (err) {
      setError(err.response?.data?.error || t('dripSchedule.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSetImmediate = async () => {
    if (!window.confirm(t('dripSchedule.confirmImmediate'))) return;
    setBulkSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/scheduling/setup-immediate/${courseId}`);
      setModules((prev) =>
        prev.map((m) => ({ ...m, release_type: 'immediate', drip_days: 0, release_date: '' }))
      );
      setSuccess(t('dripSchedule.setImmediateSuccess'));
    } catch (err) {
      setError(err.response?.data?.error || t('dripSchedule.saveFailed'));
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSetWeekly = async () => {
    setBulkSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.post(`/scheduling/setup-weekly/${courseId}`, {
        release_day: weeklyDay,
        start_with_module: false,
      });
      // Reload modules to reflect new drip_days
      const modulesRes = await api.get(`/courses/${courseId}/modules`);
      setModules(
        modulesRes.data.map((m) => ({
          id: m.id,
          title: m.title,
          order_index: m.order_index,
          release_type: m.release_type === 'weekly' ? 'days_after_enrollment' : (m.release_type || 'immediate'),
          drip_days: m.drip_days ?? 0,
          release_date: m.release_date ? m.release_date.split('T')[0] : '',
          is_preview: m.is_preview || false,
          lesson_count: parseInt(m.lesson_count) || 0,
        }))
      );
      setSuccess(t('dripSchedule.setWeeklySuccess'));
      setShowWeeklyModal(false);
    } catch (err) {
      setError(err.response?.data?.error || t('dripSchedule.saveFailed'));
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) return <div className="loading">{t('dripSchedule.eyebrow')}</div>;

  return (
    <div className="page drip-schedule-page">
      <div className="container">
        {/* Hero */}
        <section className="drip-hero">
          <div className="hero-content">
            <p className="eyebrow">{t('dripSchedule.eyebrow')}</p>
            <h1>{t('dripSchedule.title')}</h1>
            {courseName && <p>{courseName}</p>}
          </div>
          <div className="hero-actions">
            <button className="btn-secondary" onClick={() => navigate(-1)} disabled={saving}>
              {t('dripSchedule.cancel')}
            </button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || bulkSaving}>
              {saving ? t('dripSchedule.saving') : t('dripSchedule.saveAll')}
            </button>
          </div>
        </section>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Bulk Actions */}
        <section className="drip-bulk-section">
          <h2>{t('dripSchedule.quickSetup')}</h2>
          <p className="section-desc">{t('dripSchedule.quickSetupDesc')}</p>
          <div className="bulk-actions">
            <button
              className="bulk-btn immediate-btn"
              onClick={handleSetImmediate}
              disabled={bulkSaving || saving}
            >
              <span className="bulk-icon">⚡</span>
              <span className="bulk-label">{t('dripSchedule.setAllImmediate')}</span>
              <span className="bulk-hint">{t('dripSchedule.setAllImmediateHint')}</span>
            </button>
            <button
              className="bulk-btn weekly-btn"
              onClick={() => setShowWeeklyModal(true)}
              disabled={bulkSaving || saving}
            >
              <span className="bulk-icon">📅</span>
              <span className="bulk-label">{t('dripSchedule.configWeekly')}</span>
              <span className="bulk-hint">{t('dripSchedule.configWeeklyHint')}</span>
            </button>
          </div>
        </section>

        {/* Modules */}
        <section className="drip-modules-section">
          <div className="section-header">
            <h2>{t('dripSchedule.modulesTitle')} ({modules.length})</h2>
            <span className="section-hint">{t('dripSchedule.modulesHint')}</span>
          </div>

          {modules.length === 0 ? (
            <div className="empty-modules">
              <p>{t('dripSchedule.noModules')}</p>
              <button className="btn-secondary" onClick={() => navigate(`/edit-course/${courseId}`)}>
                {t('dripSchedule.goCourseEditor')}
              </button>
            </div>
          ) : (
            <div className="modules-list">
              {[...modules]
                .sort((a, b) => a.order_index - b.order_index)
                .map((module, index) => (
                  <div key={module.id} className="module-schedule-card">
                    <div className="module-card-header">
                      <div className="module-identity">
                        <span className="module-number">{t('dripSchedule.moduleLabel')} {index + 1}</span>
                        <span className="module-title">{module.title}</span>
                        {module.lesson_count > 0 && (
                          <span className="lesson-count">{module.lesson_count} lesson{module.lesson_count !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                      <div className="module-status">
                        {module.release_type === 'immediate' && (
                          <span className="status-pill status-immediate">{t('dripSchedule.immediate')}</span>
                        )}
                        {module.release_type === 'days_after_enrollment' && (
                          <span className="status-pill status-drip">Day {module.drip_days || 0}</span>
                        )}
                        {module.release_type === 'fixed_date' && (
                          <span className="status-pill status-fixed">{t('dripSchedule.statusFixed')}</span>
                        )}
                        {module.release_type === 'weekly' && (
                          <span className="status-pill status-weekly">{t('dripSchedule.weekLabel')} {Math.floor((module.drip_days || 0) / 7)}</span>
                        )}
                        {module.is_preview && (
                          <span className="status-pill status-preview">{t('dripSchedule.statusPreview')}</span>
                        )}
                      </div>
                    </div>

                    <div className="module-card-body">
                      <div className="schedule-row">
                        <div className="form-group">
                          <label>{t('dripSchedule.releaseType')}</label>
                          <select
                            value={module.release_type === 'weekly' ? 'days_after_enrollment' : module.release_type}
                            onChange={(e) => updateModule(module.id, 'release_type', e.target.value)}
                          >
                            {RELEASE_TYPES.map((rt) => (
                              <option key={rt.value} value={rt.value}>{rt.label}</option>
                            ))}
                          </select>
                        </div>

                        {(module.release_type === 'days_after_enrollment' || module.release_type === 'weekly') && (
                          <div className="form-group">
                            <label>{t('dripSchedule.daysAfterLabel')}</label>
                            <input
                              type="number"
                              min="0"
                              max="365"
                              value={module.drip_days}
                              onChange={(e) => updateModule(module.id, 'drip_days', e.target.value)}
                            />
                            <span className="field-hint">
                              {module.drip_days === 0 || module.drip_days === '0'
                                ? t('dripSchedule.availableImmediately')
                                : `${t('dripSchedule.unlocksOn')} ${module.drip_days} day${parseInt(module.drip_days) !== 1 ? 's' : ''} after enrollment`}
                            </span>
                          </div>
                        )}

                        {module.release_type === 'fixed_date' && (
                          <div className="form-group">
                            <label>{t('dripSchedule.releaseDateLabel')}</label>
                            <input
                              type="date"
                              value={module.release_date}
                              onChange={(e) => updateModule(module.id, 'release_date', e.target.value)}
                            />
                            <span className="field-hint">
                              {module.release_date
                                ? `${t('dripSchedule.unlocksOn')} ${new Date(module.release_date + 'T00:00:00').toLocaleDateString()}`
                                : t('dripSchedule.selectDate')}
                            </span>
                          </div>
                        )}
                      </div>

                      <label className="toggle-item preview-toggle">
                        <input
                          type="checkbox"
                          checked={module.is_preview}
                          onChange={(e) => updateModule(module.id, 'is_preview', e.target.checked)}
                        />
                        <span className="toggle-label">{t('dripSchedule.freePreview')}</span>
                        <span className="toggle-hint">{t('dripSchedule.freePreviewHint')}</span>
                      </label>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </section>
      </div>

      {/* Weekly Modal */}
      {showWeeklyModal && (
        <div className="modal-overlay" onClick={() => setShowWeeklyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('dripSchedule.configureWeeklyTitle')}</h2>
              <button type="button" className="modal-close" onClick={() => setShowWeeklyModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>{t('dripSchedule.configWeeklyDesc')}</p>
              <div className="form-group">
                <label>{t('dripSchedule.preferredDay')}</label>
                <select value={weeklyDay} onChange={(e) => setWeeklyDay(parseInt(e.target.value))}>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                  <option value={0}>Sunday</option>
                </select>
              </div>
              <div className="weekly-preview">
                {modules.slice(0, 4).map((m, i) => (
                  <div key={m.id} className="weekly-preview-row">
                    <span className="week-label">{t('dripSchedule.weekLabel')} {i + 1}</span>
                    <span className="week-module">{m.title}</span>
                  </div>
                ))}
                {modules.length > 4 && (
                  <p className="weekly-more">+ {modules.length - 4} more modules</p>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowWeeklyModal(false)}>
                {t('dripSchedule.cancel')}
              </button>
              <button className="btn-primary" onClick={handleSetWeekly} disabled={bulkSaving}>
                {bulkSaving ? t('dripSchedule.applying') : t('dripSchedule.apply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DripSchedule;
